import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChevronRight, ChevronDown, FolderOpen, Folder, Plus, Edit, Trash2, GripVertical, FolderPlus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Skill {
  id: string;
  name: string;
  type: string | null;
  parent_id: string | null;
  is_bucket: boolean;
  position: number;
  description: string | null;
}

interface SkillTaxonomyTreeProps {
  skills: Skill[];
  onEdit?: (skill: Skill) => void;
  onDelete?: (id: string) => void;
  onAddChild?: (parentId: string) => void;
  onMoveSkill?: (skillId: string, newParentId: string | null) => void;
  onCreateBucket?: (name: string, parentId: string | null) => void;
}

const SkillTaxonomyTree = ({ 
  skills, 
  onEdit, 
  onDelete, 
  onAddChild,
  onMoveSkill,
  onCreateBucket 
}: SkillTaxonomyTreeProps) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [draggedSkillId, setDraggedSkillId] = useState<string | null>(null);
  const [dragOverBucketId, setDragOverBucketId] = useState<string | null>(null);
  const [newBucketDialogOpen, setNewBucketDialogOpen] = useState(false);
  const [newBucketName, setNewBucketName] = useState('');
  const [newBucketParentId, setNewBucketParentId] = useState<string | null>(null);

  const toggleNode = (id: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedNodes(newExpanded);
  };

  const getChildren = (parentId: string | null): Skill[] => {
    return skills
      .filter(s => s.parent_id === parentId)
      .sort((a, b) => a.position - b.position);
  };

  // Get all buckets for dropdown (including nested)
  const getAllBuckets = (): { id: string; name: string; depth: number }[] => {
    const result: { id: string; name: string; depth: number }[] = [];
    
    const collectBuckets = (parentId: string | null, depth: number) => {
      const children = skills
        .filter(s => s.parent_id === parentId && s.is_bucket)
        .sort((a, b) => a.position - b.position);
      
      for (const bucket of children) {
        result.push({ id: bucket.id, name: bucket.name, depth });
        collectBuckets(bucket.id, depth + 1);
      }
    };
    
    collectBuckets(null, 0);
    return result;
  };

  const allBuckets = getAllBuckets();

  const getTypeColor = (type: string | null) => {
    switch (type) {
      case 'technical': return 'bg-blue-500/10 text-blue-700 dark:text-blue-400';
      case 'framework': return 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-400';
      case 'tool': return 'bg-purple-500/10 text-purple-700 dark:text-purple-400';
      case 'soft': return 'bg-pink-500/10 text-pink-700 dark:text-pink-400';
      case 'language': return 'bg-green-500/10 text-green-700 dark:text-green-400';
      case 'certification': return 'bg-orange-500/10 text-orange-700 dark:text-orange-400';
      case 'product': return 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-400';
      default: return 'bg-gray-500/10 text-gray-700 dark:text-gray-400';
    }
  };

  const handleDragStart = (e: React.DragEvent, skillId: string) => {
    setDraggedSkillId(skillId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, bucketId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverBucketId(bucketId);
  };

  const handleDragLeave = () => {
    setDragOverBucketId(null);
  };

  const handleDrop = (e: React.DragEvent, targetBucketId: string | null) => {
    e.preventDefault();
    if (draggedSkillId && onMoveSkill && draggedSkillId !== targetBucketId) {
      onMoveSkill(draggedSkillId, targetBucketId);
    }
    setDraggedSkillId(null);
    setDragOverBucketId(null);
  };

  const handleDragEnd = () => {
    setDraggedSkillId(null);
    setDragOverBucketId(null);
  };

  const handleBucketSelect = (skillId: string, bucketId: string) => {
    if (onMoveSkill) {
      const newParentId = bucketId === 'none' ? null : bucketId;
      onMoveSkill(skillId, newParentId);
    }
  };

  const handleCreateBucket = () => {
    if (newBucketName.trim() && onCreateBucket) {
      onCreateBucket(newBucketName.trim(), newBucketParentId);
      setNewBucketName('');
      setNewBucketParentId(null);
      setNewBucketDialogOpen(false);
    }
  };

  const renderNode = (skill: Skill, depth: number = 0): JSX.Element => {
    const children = getChildren(skill.id);
    const hasChildren = children.length > 0 || skill.is_bucket;
    const isExpanded = expandedNodes.has(skill.id);
    const isDragOver = dragOverBucketId === skill.id;
    const isDragging = draggedSkillId === skill.id;

    return (
      <div key={skill.id} className="select-none">
        <div
          className={cn(
            "flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-accent/50 group transition-colors",
            depth > 0 && "ml-6",
            isDragOver && skill.is_bucket && "bg-primary/10 ring-2 ring-primary/50",
            isDragging && "opacity-50"
          )}
          draggable={!skill.is_bucket}
          onDragStart={(e) => handleDragStart(e, skill.id)}
          onDragOver={(e) => skill.is_bucket && handleDragOver(e, skill.id)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => skill.is_bucket && handleDrop(e, skill.id)}
          onDragEnd={handleDragEnd}
        >
          {/* Drag handle for non-buckets */}
          {!skill.is_bucket && (
            <GripVertical className="h-4 w-4 text-muted-foreground/50 cursor-grab active:cursor-grabbing" />
          )}

          <button
            onClick={() => hasChildren && toggleNode(skill.id)}
            className={cn(
              "p-0.5 rounded hover:bg-accent",
              !hasChildren && "invisible",
              skill.is_bucket && !isDragging && "ml-0"
            )}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </button>

          {skill.is_bucket ? (
            isExpanded ? (
              <FolderOpen className="h-4 w-4 text-amber-500" />
            ) : (
              <Folder className="h-4 w-4 text-amber-500" />
            )
          ) : (
            <div className="w-4 h-4 flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-primary/60" />
            </div>
          )}

          <span className={cn("flex-1 text-sm", skill.is_bucket && "font-medium")}>
            {skill.name}
          </span>

          {/* Bucket dropdown for non-bucket skills */}
          {!skill.is_bucket && onMoveSkill && (
            <Select
              value={skill.parent_id || 'none'}
              onValueChange={(value) => handleBucketSelect(skill.id, value)}
            >
              <SelectTrigger className="h-7 w-32 text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                <SelectValue placeholder="Select bucket" />
              </SelectTrigger>
              <SelectContent className="bg-background z-50">
                <SelectItem value="none">
                  <span className="text-muted-foreground">No bucket</span>
                </SelectItem>
                {allBuckets.map((bucket) => (
                  <SelectItem key={bucket.id} value={bucket.id}>
                    <span style={{ paddingLeft: bucket.depth * 12 }}>
                      {bucket.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {!skill.is_bucket && skill.type && (
            <Badge variant="secondary" className={cn("text-xs", getTypeColor(skill.type))}>
              {skill.type}
            </Badge>
          )}

          {(onEdit || onDelete || onAddChild) && (
            <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
              {skill.is_bucket && onAddChild && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => onAddChild(skill.id)}
                  title="Add child skill"
                >
                  <Plus className="h-3 w-3" />
                </Button>
              )}
              {onEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => onEdit(skill)}
                  title="Edit"
                >
                  <Edit className="h-3 w-3" />
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => onDelete(skill.id)}
                  title="Delete"
                >
                  <Trash2 className="h-3 w-3 text-destructive" />
                </Button>
              )}
            </div>
          )}
        </div>

        {isExpanded && children.map(child => renderNode(child, depth + 1))}
      </div>
    );
  };

  const rootNodes = getChildren(null);

  return (
    <>
      {/* Header with create bucket button */}
      <div className="flex items-center justify-between mb-4 pb-3 border-b">
        <div className="text-sm text-muted-foreground">
          Drag skills to buckets or use the dropdown to organize
        </div>
        {onCreateBucket && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setNewBucketDialogOpen(true)}
          >
            <FolderPlus className="h-4 w-4 mr-2" />
            New Bucket
          </Button>
        )}
      </div>

      {/* Drop zone for removing from bucket (root level) */}
      {draggedSkillId && (
        <div
          className={cn(
            "mb-2 p-3 border-2 border-dashed rounded-md text-center text-sm text-muted-foreground transition-colors",
            dragOverBucketId === 'root' ? "border-primary bg-primary/5" : "border-muted"
          )}
          onDragOver={(e) => { e.preventDefault(); setDragOverBucketId('root'); }}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, null)}
        >
          Drop here to remove from bucket
        </div>
      )}

      {rootNodes.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Folder className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No skills in taxonomy yet</p>
          <p className="text-sm">Create buckets to organize your skills</p>
        </div>
      ) : (
        <div className="space-y-1">
          {rootNodes.map(node => renderNode(node))}
        </div>
      )}

      {/* New Bucket Dialog */}
      <Dialog open={newBucketDialogOpen} onOpenChange={setNewBucketDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Bucket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="bucket-name">Bucket Name</Label>
              <Input
                id="bucket-name"
                value={newBucketName}
                onChange={(e) => setNewBucketName(e.target.value)}
                placeholder="e.g., Frontend, Backend, DevOps"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="parent-bucket">Parent Bucket (optional)</Label>
              <Select
                value={newBucketParentId || 'none'}
                onValueChange={(value) => setNewBucketParentId(value === 'none' ? null : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select parent bucket" />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  <SelectItem value="none">
                    <span className="text-muted-foreground">No parent (root level)</span>
                  </SelectItem>
                  {allBuckets.map((bucket) => (
                    <SelectItem key={bucket.id} value={bucket.id}>
                      <span style={{ paddingLeft: bucket.depth * 12 }}>
                        {bucket.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Create nested buckets by selecting a parent bucket
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewBucketDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateBucket} disabled={!newBucketName.trim()}>
              Create Bucket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SkillTaxonomyTree;
